/**
 * PJ モードのまま次の画面へ送るためのリンク条件。
 *
 * 一覧から詳細へ飛ぶリンクが PJ 番号を落とすと、PJ を適用した後の姿を見に行ったつもりで
 * マスターが開く。**落ちたことは画面に出ない**ので、利用者は気づけないまま現在版を読む。
 * `PJ_SEARCH_ROUTES` の画面と `components/spec/SpecNodeLink.tsx` が同じ判断をするので、条件の
 * 組み立てをここ 1 箇所に置く。導線ごとに書き写すと、片方だけ条件を変えたときに経路によって
 * PJ が落ちる。
 *
 * PJ を渡していないときに `search` を付けないのは、共有されたリンクに意味の無い条件が写るのを
 * 避けるためである。PJ モードかどうかの判定そのものは `isPjMode` が持つ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ モードを次の画面へ引き継ぐリンクの検索条件を組む。
 * @graph-connects isPjMode [calls] PJ モードかどうかの判定
 * @graph-connects searchValueOf [calls] 検索条件に置く形へ直す
 */
import { isPjMode } from '../lib/pj-mode.js';
import { searchValueOf } from './pj-search.js';

/** リンクへ広げる検索条件。PJ モードのときだけ `search` を持つ。 */
export interface PjLinkSearch {
  /** 数字だけの番号は数値で載せる（`searchValueOf`）。 */
  search?: { pj: number | string };
}

/**
 * PJ 番号からリンクへ広げる検索条件を組む。
 *
 * **数字だけの番号は数値で載せる。** 文字列で載せると、ルーターの既定の書き出しが引用符で包み、
 * 押す前のリンクの `href` に `?pj=%22139162%22` が出る。押した後は行き先の検証器が数値へ
 * 直すので、`href` だけが別の綴りになる。
 *
 * @param pj 重ねる PJ 番号。PJ モードでなければ undefined
 * @returns リンクへ広げる検索条件
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ 番号からリンクの検索条件を組み、数字だけの番号は数値で載せ、PJ モードでなければ何も足さない。
 * @graph-connects isPjMode [calls] PJ モードかどうかの判定
 * @graph-connects searchValueOf [calls] 検索条件に置く形へ直す
 */
export function pjLinkSearch(pj: string | undefined): PjLinkSearch {
  return isPjMode(pj) ? { search: { pj: searchValueOf(pj) } } : {};
}
